import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getToken, getUser, authHeader } from '../auth/auth';
import AppDownloadModal from './AppDownloadModal';

// ─────────────────────────────────────────────────────────────────────────────
// Website event page — the web counterpart of the mobile ViewOneEventScreen.
// Loaded from share links (/event/:slug[/:sharingUser]); fetches the event by
// slug-or-id and renders an app-styled, single-column layout: hero image
// gallery, title + date/time, host, description, occurrences, related events,
// and like / share / tickets actions.
// ─────────────────────────────────────────────────────────────────────────────

const API = 'https://maineventbackend.seuytel.com/api/';
const BACKEND_ROOT = 'https://maineventbackend.seuytel.com';
const STORAGE = `${BACKEND_ROOT}/storage/`;

const ACCENT = '#4E6FD6';
const ACCENT_SOFT = '#5B8FE8';
const SCREEN_BG = '#F6F7FB';
const TEXT_DARK = '#1B2559';
const TEXT_MUTED = '#707EAE';
const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// ── App deep-linking (mirrors share-preview.blade.php) ───────────────────────
// The "use the app" interstitial (AppDownloadModal) offers this deep link on
// phones; the store badges inside it handle the not-installed case.
const APP_SCHEME = 'mainevent://event/'; // + <eventId>[/<sharingUserId>]

// Default avatars (served from the website's public/ folder; fall back to a
// neutral silhouette data-uri if those assets are absent).
const FALLBACK_AVATAR =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="#E7EEFC"/><circle cx="40" cy="32" r="15" fill="#B9C7E8"/><rect x="16" y="52" width="48" height="26" rx="13" fill="#B9C7E8"/></svg>`,
    );

interface EventDate {
    start_date?: string | null;
    end_date?: string | null;
}
interface EventData {
    id: number;
    title?: string;
    slug?: string;
    description?: string;
    images?: string[];
    host?: number;
    currency?: string;
    status?: number;
    is_recurring?: boolean;
    is_public?: boolean;
    is_online?: boolean;
    event_category?: any;
    is_event_pool?: boolean;
    date?: EventDate[];
    sub_events?: EventData[];
    location?: any;
    city?: string;
    [key: string]: any;
}
interface HostUser {
    id: number;
    first_name?: string;
    last_name?: string;
    profile_photo_url?: string | null;
    profile_photo_path?: string | null;
    anrede?: number;
    user_type?: number;
}

const USER_TYPE_LABELS: Record<number, string> = {
    1: 'Privat',
    2: 'Veranstalter',
    3: 'Firma',
    4: 'Dienstleister',
};

// Mirror ProfileScreen.resolveProfileAvatarUri so host photos resolve the same
// way as in the app (absolute/file URLs, /storage, storage/, leading slash, bare).
function resolveAvatarUri(raw?: string | null): string | null {
    const value = String(raw ?? '').trim();
    if (!value) return null;
    if (/^(https?:|file:)/.test(value)) return value;
    if (value.startsWith('/storage/')) return `${BACKEND_ROOT}${value}`;
    if (value.startsWith('storage/')) return `${BACKEND_ROOT}/${value}`;
    if (value.startsWith('/')) return `${BACKEND_ROOT}${value}`;
    return `${STORAGE}${value}`;
}

function imageUrl(img?: string): string {
    if (!img) return '';
    return /^https?:/.test(img) ? img : `${STORAGE}${img}`;
}

// ── Date / time formatting (German locale, matching the app) ─────────────────
function fmtDateLong(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
}
function fmtTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function fmtChip(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}
function fmtDuration(startIso?: string | null, endIso?: string | null): string {
    if (!startIso || !endIso) return '';
    const s = new Date(startIso).getTime();
    const e = new Date(endIso).getTime();
    const diff = e - s;
    if (!(diff > 0)) return '';
    const days = Math.floor(diff / 86400000);
    if (days >= 1) return `${days} ${days === 1 ? 'Tag' : 'Tage'}`;
    const hours = Math.floor(diff / 3600000);
    if (hours >= 1) return `${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
    const mins = Math.floor(diff / 60000);
    return `${mins} ${mins === 1 ? 'Minute' : 'Minuten'}`;
}

function formatAddress(loc: any): string {
    if (!loc) return '';
    const o = Array.isArray(loc) ? loc[0] : loc;
    if (!o || typeof o !== 'object') return typeof loc === 'string' ? loc : '';
    const street = o.street || '';
    const hn = o.housenumber || o.house_number || '';
    const zip = o.postal_code || o.zip || '';
    const city = o.city || '';
    const country = o.country || '';
    return [[street, hn].filter(Boolean).join(' '), [zip, city].filter(Boolean).join(' '), country]
        .filter(Boolean)
        .join(', ');
}

function renderFormattedDescription(text: string): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    const boldPattern = /(\*\*|__|\*|_)(?=\S)([^\r\n]*?\S)\1/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = boldPattern.exec(text)) !== null) {
        const [raw, , content] = match;
        if (match.index > lastIndex) {
            nodes.push(text.slice(lastIndex, match.index));
        }
        nodes.push(<strong key={`bold-${match.index}`}>{content}</strong>);
        lastIndex = match.index + raw.length;
    }

    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }

    return nodes.length ? nodes : [text];
}

const DESCRIPTION_ALLOWED_TAGS = new Set([
    'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4',
    'h5', 'h6', 'hr', 'i', 'li', 'ol', 'p', 'pre', 's', 'span', 'strong', 'u', 'ul',
]);

const DESCRIPTION_ALLOWED_ATTRS: Record<string, Set<string>> = {
    a: new Set(['href', 'title', 'target', 'rel']),
    '*': new Set(['aria-label']),
};

function isProbablyHtml(text: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(text);
}

function sanitizeDescriptionHtml(html: string): string {
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return '';
    }

    const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return '';

    const sanitizeNode = (node: Node) => {
        Array.from(node.childNodes).forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const el = child as HTMLElement;
                const tag = el.tagName.toLowerCase();
                if (!DESCRIPTION_ALLOWED_TAGS.has(tag)) {
                    el.replaceWith(...Array.from(el.childNodes));
                    return;
                }

                Array.from(el.attributes).forEach((attr) => {
                    const name = attr.name.toLowerCase();
                    const allowed = DESCRIPTION_ALLOWED_ATTRS[tag]?.has(name) || DESCRIPTION_ALLOWED_ATTRS['*'].has(name);
                    if (!allowed || name.startsWith('on') || name === 'style') {
                        el.removeAttribute(attr.name);
                    }
                });

                if (tag === 'a') {
                    const href = el.getAttribute('href') || '';
                    if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) {
                        el.removeAttribute('href');
                    }
                    el.setAttribute('target', '_blank');
                    el.setAttribute('rel', 'noreferrer');
                }

                sanitizeNode(el);
                return;
            }

            if (child.nodeType !== Node.TEXT_NODE) {
                child.remove();
            }
        });
    };

    sanitizeNode(root);
    return root.innerHTML;
}

// ── Tiny inline icons ────────────────────────────────────────────────────────
const Icon = {
    back: (c = TEXT_DARK) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
    share: (c = TEXT_DARK) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v14" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
    heart: (filled: boolean, c = TEXT_DARK) => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? '#E0245E' : 'none'}><path d="M12 21s-7.5-4.6-10-9.2C.5 8.5 2.3 5 5.8 5 8 5 9.5 6.5 12 9c2.5-2.5 4-4 6.2-4 3.5 0 5.3 3.5 3.8 6.8C19.5 16.4 12 21 12 21z" stroke={filled ? '#E0245E' : c} strokeWidth="2" strokeLinejoin="round" /></svg>
    ),
    calendar: (c = ACCENT) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke={c} strokeWidth="2" /><path d="M3 9h18M8 3v4M16 3v4" stroke={c} strokeWidth="2" strokeLinecap="round" /></svg>
    ),
    clock: (c = ACCENT) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="2" /><path d="M12 7v5l3 2" stroke={c} strokeWidth="2" strokeLinecap="round" /></svg>
    ),
    pin: (c = ACCENT) => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 22s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z" stroke={c} strokeWidth="2" strokeLinejoin="round" /><circle cx="12" cy="10" r="2.5" stroke={c} strokeWidth="2" /></svg>
    ),
};

const EventViewComponent: React.FC = () => {
    const { slug, sharingUser } = useParams();
    const navigate = useNavigate();

    const [event, setEvent] = useState<EventData | null>(null);
    const [host, setHost] = useState<HostUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [activeImage, setActiveImage] = useState(0);
    const [lightbox, setLightbox] = useState(false);
    const [descExpanded, setDescExpanded] = useState(false);
    const [selectedDateIdx, setSelectedDateIdx] = useState(0);
    const [selectedSubEventId, setSelectedSubEventId] = useState<number | string | null>(null);
    const [liked, setLiked] = useState(false);
    const [toast, setToast] = useState('');
    const [ticketGateKey, setTicketGateKey] = useState(0);

    const [related, setRelated] = useState<EventData[]>([]);
    const [relatedLabel, setRelatedLabel] = useState('');
    const [categories, setCategories] = useState<any[]>([]);

    const galleryRef = useRef<HTMLDivElement>(null);
    const descRef = useRef<HTMLDivElement>(null);
    const [descCanExpand, setDescCanExpand] = useState(false);

    // ── Load event ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!slug) {
            setError('Kein Event angegeben.');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        fetch(`${API}events/getOneEvent/${encodeURIComponent(slug)}`)
            .then((r) => {
                if (!r.ok) throw new Error('Event konnte nicht geladen werden.');
                return r.json();
            })
            .then((json) => {
                if (cancelled) return;
                const ev: EventData = json.data ?? json;
                setEvent(ev);
                const u = getUser();
                if (u?.liked_events && Array.isArray(u.liked_events)) {
                    setLiked(u.liked_events.includes(ev.id));
                }
            })
            .catch((e) => !cancelled && setError(e.message || 'Etwas ist schiefgelaufen.'))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // ── Load host ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const hostId = event?.host;
        if (!hostId) return;
        let cancelled = false;
        fetch(`${API}one-user/${hostId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((u) => {
                if (cancelled || !u) return;
                setHost(u.data ?? u);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [event?.host]);

    // ── Load related events (similar → host's other public events) ─────────────
    useEffect(() => {
        const ev = event;
        if (!ev?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const sim = await fetch(`${API}events/${ev.id}/similar`);
                if (sim.ok) {
                    const data: EventData[] = (await sim.json()).data ?? [];
                    if (data.length && !cancelled) {
                        setRelated(data.slice(0, 8));
                        setRelatedLabel('Das könnte dich auch interessieren');
                        return;
                    }
                }
            } catch {}
            if (!ev.host || cancelled) return;
            try {
                const h = await fetch(`${API}events/host/${ev.host}/public`);
                if (h.ok) {
                    const data: EventData[] = (await h.json()).data ?? [];
                    const filtered = data.filter((e) => e.id !== ev.id);
                    if (filtered.length && !cancelled) {
                        setRelated(filtered.slice(0, 8));
                        setRelatedLabel('Weitere Events dieses Veranstalters');
                    }
                }
            } catch {}
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.id, event?.host]);

    // ── Preselect next upcoming occurrence for recurring events ────────────────
    useEffect(() => {
        if (event?.is_recurring && Array.isArray(event.date) && event.date.length) {
            const now = Date.now();
            const idx = event.date.findIndex((d) => {
                const cmp = d.end_date || d.start_date;
                return cmp && new Date(cmp).getTime() >= now;
            });
            if (idx >= 0) setSelectedDateIdx(idx);
        }
    }, [event]);

    useEffect(() => {
        if (!event?.is_event_pool || !Array.isArray(event.sub_events) || !event.sub_events.length) {
            setSelectedSubEventId(null);
            return;
        }

        const matched = event.sub_events.find((sub) => String(sub.id) === String(slug));
        setSelectedSubEventId((matched || event.sub_events[0])?.id ?? null);
        setSelectedDateIdx(0);
        setActiveImage(0);
    }, [event, slug]);

    // ── Load categories (to resolve event_category → names) ────────────────────
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}events/getCategories`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
                if (cancelled || !j) return;
                setCategories(Array.isArray(j.data) ? j.data : Array.isArray(j) ? j : []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    // ── Social / link-preview meta tags (mirrors share-preview.blade.php) ───────
    // Foreign apps that execute JS (Slack, Telegram, Google) pick these up; pure
    // crawlers (WhatsApp/Facebook) are routed to the backend share-preview at the
    // web-server level — see maineventswebsite/.htaccess.
    useEffect(() => {
        if (!event) return;
        const prevTitle = document.title;

        const title = event.title || 'MainEvents';
        const desc = `Entdecke ${event.title ?? 'dieses Event'} auf Seuytel · MainEvents – Details und Tickets ansehen.`;
        const firstImage = event.images && event.images.length ? event.images[0] : '';
        const img = firstImage ? imageUrl(firstImage) : `${window.location.origin}/mainevents_icon.png`;
        const url = `${window.location.origin}/event/${event.slug || event.id}${sharingUser ? `/${sharingUser}` : ''}`;

        document.title = title;

        const metas: Array<['property' | 'name', string, string]> = [
            ['property', 'og:title', title],
            ['property', 'og:description', desc],
            ['property', 'og:image', img],
            ['property', 'og:image:secure_url', img],
            ['property', 'og:url', url],
            ['property', 'og:type', 'website'],
            ['name', 'description', desc],
            ['name', 'twitter:card', 'summary_large_image'],
            ['name', 'twitter:title', title],
            ['name', 'twitter:description', desc],
            ['name', 'twitter:image', img],
        ];

        const created: Element[] = [];
        const upsert = (attr: 'property' | 'name', key: string, content: string) => {
            let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
                created.push(el);
            }
            el.setAttribute('content', content);
        };
        metas.forEach(([attr, key, content]) => upsert(attr, key, content));

        let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        const createdCanonical = !canonical;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);

        return () => {
            document.title = prevTitle;
            created.forEach((el) => el.remove());
            if (createdCanonical && canonical) canonical.remove();
        };
    }, [event, sharingUser]);

    // App deep link for the "use the app" interstitial (AppDownloadModal), which
    // replaces the old mobile-only auto-handoff sheet and shows on every visit,
    // on every screen size.
    const buildAppLink = () =>
        `${APP_SCHEME}${event?.id ?? ''}${sharingUser ? `/${sharingUser}` : ''}`;

    const showToast = (msg: string) => {
        setToast(msg);
        window.setTimeout(() => setToast(''), 2200);
    };

    const selectedSubEvent = event?.is_event_pool && Array.isArray(event.sub_events)
        ? event.sub_events.find((sub) => String(sub.id) === String(selectedSubEventId)) || event.sub_events[0]
        : null;
    const displayEvent = selectedSubEvent || event;
    const description = displayEvent?.description || '';
    const descriptionIsHtml = useMemo(() => isProbablyHtml(description), [description]);
    const sanitizedDescriptionHtml = useMemo(
        () => (descriptionIsHtml ? sanitizeDescriptionHtml(description) : ''),
        [description, descriptionIsHtml],
    );
    const images = useMemo(
        () => displayEvent?.images && displayEvent.images.length
            ? displayEvent.images
            : event?.images && event.images.length
                ? event.images
                : [],
        [displayEvent, event],
    );
    const dates = useMemo(
        () => displayEvent?.date ?? event?.date ?? [],
        [displayEvent, event],
    );
    const activeDate = dates[selectedDateIdx] || dates[0];

    const lastEndMs = useMemo(
        () =>
            (dates ?? []).reduce((max, d) => {
                const raw = d?.end_date ?? d?.start_date;
                const t = raw ? new Date(raw).getTime() : NaN;
                return Number.isFinite(t) && t > max ? t : max;
            }, 0),
        [dates],
    );
    const isPast = lastEndMs > 0 && lastEndMs < Date.now();
    const bookingDisabled = event?.status === 4 || event?.status === 6 || isPast;

    useEffect(() => {
        setDescExpanded(false);
        setDescCanExpand(false);
    }, [description]);

    useEffect(() => {
        if (!description || descExpanded) return;
        let frame = 0;
        const measure = () => {
            const el = descRef.current;
            if (!el) return;
            setDescCanExpand(el.scrollHeight > el.clientHeight + 1);
        };
        frame = window.requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
        };
    }, [description, descExpanded]);

    // ── Actions ────────────────────────────────────────────────────────────────
    const onShare = async () => {
        if (!displayEvent) return;
        const link = `${window.location.origin}/event/${displayEvent.slug || displayEvent.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: displayEvent.title, text: displayEvent.title, url: link });
            } else {
                await navigator.clipboard.writeText(link);
                showToast('Link kopiert');
            }
        } catch {
            /* user cancelled share */
        }
        // Fire-and-forget share count (public endpoint).
        fetch(`${API}events/${displayEvent.id}/share`, { method: 'POST' }).catch(() => {});
    };

    const onToggleLike = async () => {
        const currentEvent = displayEvent;
        if (!event || !currentEvent) return;
        if (!getToken()) {
            navigate(`/login?redirect=${encodeURIComponent(`/event/${event.slug || event.id}`)}`);
            return;
        }
        const next = !liked;
        setLiked(next); // optimistic
        try {
            const res = await fetch(`${API}events/${currentEvent.id}/${next ? 'like' : 'unlike'}`, {
                method: 'POST',
                headers: { ...authHeader() },
            });
            if (!res.ok) setLiked(!next); // revert
        } catch {
            setLiked(!next);
        }
    };

    const goTickets = () => {
        if (!displayEvent || bookingDisabled) return;
        setTicketGateKey((key) => key + 1);
    };

    const toggleDescription = () => {
        const scrollTop = window.scrollY;
        setDescExpanded((v) => !v);
        window.requestAnimationFrame(() => {
            window.scrollTo({ top: scrollTop, behavior: 'auto' });
            window.requestAnimationFrame(() => window.scrollTo({ top: scrollTop, behavior: 'auto' }));
        });
    };

    // ── Render states ──────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={styles.center}>
                <style>{spinKeyframes}</style>
                <div style={styles.spinner} />
            </div>
        );
    }
    if (error || !event) {
        return (
            <div style={styles.center}>
                <p style={{ color: TEXT_MUTED, fontFamily: FONT_FAMILY }}>{error || 'Event nicht gefunden.'}</p>
            </div>
        );
    }

    const visibleEvent = displayEvent ?? event;
    const hostName = host ? `${host.first_name ?? ''} ${host.last_name ?? ''}`.trim() : '';
    const hostAvatar = resolveAvatarUri(host?.profile_photo_url || host?.profile_photo_path) || FALLBACK_AVATAR;
    const isTheBowlingClubHost = Number(event.host) === 55 || Number(host?.id) === 55;
    const address = formatAddress(visibleEvent.location) || visibleEvent.city || '';

    // ── Allgemeine Informationen (general info) values ─────────────────────────
    const categoryNames = (() => {
        const ec = visibleEvent.event_category;
        if (!ec) return '';
        const arr = Array.isArray(ec) ? ec : [ec];
        return arr
            .map((item: any) => {
                if (item && typeof item === 'object') return item.name || item.title || '';
                const found = categories.find((c) => String(c.id) === String(item));
                return found?.name || '';
            })
            .filter(Boolean)
            .join(', ');
    })();

    const sameDay =
        activeDate?.start_date &&
        activeDate?.end_date &&
        new Date(activeDate.start_date).toDateString() === new Date(activeDate.end_date).toDateString();
    const dateValue = activeDate?.start_date
        ? sameDay || !activeDate.end_date
            ? fmtDateLong(activeDate.start_date)
            : `${fmtDateLong(activeDate.start_date)} – ${fmtDateLong(activeDate.end_date)}`
        : '';
    const timeValue = activeDate?.start_date
        ? `${fmtTime(activeDate.start_date)}${activeDate.end_date ? ` – ${fmtTime(activeDate.end_date)}` : ''}`
        : '';
    const durationValue = fmtDuration(activeDate?.start_date, activeDate?.end_date);

    const infoItem = (label: string, value?: React.ReactNode | null) =>
        value ? (
            <div style={styles.infoItem}>
                <span style={styles.infoItemLabel}>{label}</span>
                <span style={styles.infoItemValue}>{value}</span>
            </div>
        ) : null;

    const organizerValue = hostName
        ? isTheBowlingClubHost
            ? (
                <a href="/thebowlingclub" target="_blank" rel="noreferrer" style={styles.infoLink}>
                    {hostName}
                </a>
            )
            : hostName
        : null;

    return (
        <div style={styles.page}>
            <style>{spinKeyframes}</style>

            <div style={styles.column}>
                {/* ── Hero gallery ── */}
                <div style={styles.hero}>
                    {images.length > 0 ? (
                        <div
                            ref={galleryRef}
                            style={styles.gallery}
                            onScroll={(e) => {
                                const el = e.currentTarget;
                                const idx = Math.round(el.scrollLeft / el.clientWidth);
                                if (idx !== activeImage) setActiveImage(idx);
                            }}
                        >
                            {images.map((img, i) => (
                                <img
                                    key={i}
                                    src={imageUrl(img)}
                                    alt={visibleEvent.title || event.title || 'Event'}
                                    style={styles.heroImg}
                                    onClick={() => setLightbox(true)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div style={{ ...styles.heroImg, background: '#DDE4F2' }} />
                    )}

                    {/* Top bar over the image */}
                    <div style={styles.heroBar}>
                        <button style={styles.circleBtn} onClick={() => navigate(-1)} aria-label="Zurück">
                            {Icon.back()}
                        </button>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button style={styles.circleBtn} onClick={onToggleLike} aria-label="Merken">
                                {Icon.heart(liked)}
                            </button>
                            <button style={styles.circleBtn} onClick={onShare} aria-label="Teilen">
                                {Icon.share()}
                            </button>
                        </div>
                    </div>

                    {images.length > 1 && (
                        <div style={styles.counter}>
                            {activeImage + 1} / {images.length}
                        </div>
                    )}
                </div>

                {/* ── Body card ── */}
                <div style={styles.body}>
                    {(bookingDisabled) && (
                        <div style={styles.notice}>
                            {event.status === 4 || event.status === 6
                                ? 'Dieses Event wurde abgesagt.'
                                : 'Dieses Event hat bereits stattgefunden.'}
                        </div>
                    )}

                    {event.is_event_pool && (
                        <div style={styles.poolBadge}>Event-Pool</div>
                    )}

                    <h1 style={styles.title}>{visibleEvent.title}</h1>

                    {event.is_event_pool && Array.isArray(event.sub_events) && event.sub_events.length > 0 && (
                        <div style={styles.poolSelector}>
                            {event.sub_events.map((sub) => {
                                const active = String(sub.id) === String(visibleEvent.id);
                                const first = sub.date?.[0];
                                return (
                                    <button
                                        key={sub.id}
                                        type="button"
                                        style={{ ...styles.poolOption, ...(active ? styles.poolOptionActive : {}) }}
                                        onClick={() => {
                                            setSelectedSubEventId(sub.id);
                                            setSelectedDateIdx(0);
                                            setActiveImage(0);
                                        }}
                                    >
                                        <span style={styles.poolOptionTitle}>{sub.title || 'Event'}</span>
                                        {first?.start_date && <span style={styles.poolOptionDate}>{fmtChip(first.start_date)}</span>}
                                        <span style={styles.poolOptionPrice}>{sub.ticket_price && sub.ticket_price > 0 ? `${sub.currency || event.currency || 'EUR'} ${sub.ticket_price}` : 'Free'}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Recurring occurrence chips */}
                    {visibleEvent.is_recurring && dates.length > 1 && (
                        <div style={styles.chipRow}>
                            {dates.map((d, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSelectedDateIdx(i)}
                                    style={{
                                        ...styles.chip,
                                        ...(i === selectedDateIdx ? styles.chipActive : {}),
                                    }}
                                >
                                    {fmtChip(d.start_date)}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Date / time */}
                    {activeDate?.start_date && (
                        <div style={styles.infoRow}>
                            {Icon.calendar()}
                            <div>
                                <div style={styles.infoStrong}>{fmtDateLong(activeDate.start_date)}</div>
                                <div style={styles.infoSub}>
                                    {fmtTime(activeDate.start_date)}
                                    {activeDate.end_date ? ` – ${fmtTime(activeDate.end_date)}` : ''}
                                    {fmtDuration(activeDate.start_date, activeDate.end_date)
                                        ? `  ·  ${fmtDuration(activeDate.start_date, activeDate.end_date)}`
                                        : ''}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Location */}
                    {(address || visibleEvent.is_online) && (
                        <div style={styles.infoRow}>
                            {Icon.pin()}
                            <div style={styles.infoStrong}>{visibleEvent.is_online ? 'Online-Event' : address}</div>
                        </div>
                    )}

                    {!visibleEvent.is_online && address && (
                        <div style={styles.mapPreviewCard}>
                            <div style={styles.mapPreviewGrid}>
                                <span />
                                <span />
                                <span />
                                <span />
                            </div>
                            <div style={styles.mapPreviewContent}>
                                <div style={styles.mapPreviewTitle}>Karte</div>
                                <div style={styles.mapPreviewAddress}>{address}</div>
                                <button type="button" style={styles.mapPreviewBtn} onClick={() => showToast('Die interaktive Karte ist in der App verfügbar.')}>
                                    Karte in der App ansehen
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Host */}
                    {host && (
                        isTheBowlingClubHost ? (
                            <a href="/thebowlingclub" target="_blank" rel="noreferrer" style={styles.hostCard}>
                                <img src={hostAvatar} alt={hostName} style={styles.hostAvatar} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={styles.hostName}>{hostName || 'Veranstalter'}</div>
                                    <div style={styles.hostType}>
                                        {USER_TYPE_LABELS[host.user_type ?? 0] || 'Veranstalter'}
                                    </div>
                                </div>
                            </a>
                        ) : (
                            <div style={styles.hostCard}>
                                <img src={hostAvatar} alt={hostName} style={styles.hostAvatar} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={styles.hostName}>{hostName || 'Veranstalter'}</div>
                                    <div style={styles.hostType}>
                                        {USER_TYPE_LABELS[host.user_type ?? 0] || 'Veranstalter'}
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Description */}
                    {description && (
                        <div style={{ marginTop: 18 }}>
                            <div style={styles.sectionLabel}>Beschreibung</div>
                            <div
                                ref={descRef}
                                style={{
                                    ...styles.desc,
                                    ...(!descExpanded ? styles.descClamp : {}),
                                }}
                            >
                                {descriptionIsHtml ? (
                                    <div
                                        style={styles.descHtml}
                                        dangerouslySetInnerHTML={{ __html: sanitizedDescriptionHtml }}
                                    />
                                ) : (
                                    renderFormattedDescription(description)
                                )}
                            </div>
                            {descCanExpand && (
                                <button
                                    type="button"
                                    style={styles.moreBtn}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={toggleDescription}
                                >
                                    {descExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Allgemeine Informationen (general info) */}
                    <div style={{ marginTop: 22 }}>
                        <div style={styles.sectionLabel}>Allgemeine Informationen</div>
                        <div style={styles.infoCard}>
                            {infoItem('Datum', dateValue)}
                            {infoItem('Uhrzeit', timeValue)}
                            {infoItem('Dauer', durationValue)}
                            {infoItem('Format', visibleEvent.is_online ? 'Online' : 'Vor Ort')}
                            {!visibleEvent.is_online && infoItem('Ort', address)}
                            {infoItem('Veranstaltungsart', visibleEvent.is_public ? 'Öffentlich' : 'Privat')}
                            {infoItem('Kategorie', categoryNames)}
                            {infoItem('Veranstalter', organizerValue)}
                        </div>
                    </div>

                    {/* Related events */}
                    {related.length > 0 && (
                        <div style={{ marginTop: 22 }}>
                            <div style={styles.sectionLabel}>{relatedLabel}</div>
                            <div style={styles.relRow}>
                                {related.map((r) => (
                                    <RelatedCard key={r.id} ev={r} onOpen={() => navigate(`/event/${r.slug || r.id}`)} />
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ height: 90 }} />
                </div>

                {/* ── Sticky footer CTA ── */}
                <div style={styles.footer}>
                    <button
                        style={{ ...styles.cta, ...(bookingDisabled ? styles.ctaDisabled : {}) }}
                        onClick={goTickets}
                        disabled={bookingDisabled}
                    >
                        {bookingDisabled ? 'Nicht verfügbar' : 'Tickets ansehen'}
                    </button>
                </div>
            </div>

            {/* ── Lightbox ── */}
            {lightbox && images.length > 0 && (
                <div style={styles.lightbox} onClick={() => setLightbox(false)}>
                    <button style={styles.lightboxClose} onClick={() => setLightbox(false)}>✕</button>
                    <img src={imageUrl(images[activeImage])} alt={event.title || ''} style={styles.lightboxImg} onClick={(e) => e.stopPropagation()} />
                </div>
            )}

            {/* ── "Use the app" interstitial — slides up on every visit ── */}
            <AppDownloadModal deepLink={buildAppLink()} reopenKey={ticketGateKey} />

            {toast && <div style={styles.toast}>{toast}</div>}
        </div>
    );
};

// ── Related / sub-event card ─────────────────────────────────────────────────
const RelatedCard: React.FC<{ ev: EventData; onOpen: () => void }> = ({ ev, onOpen }) => {
    const img = ev.images && ev.images.length ? imageUrl(ev.images[0]) : '';
    const first = ev.date && ev.date.length ? ev.date[0] : undefined;
    return (
        <button style={styles.relCard} onClick={onOpen}>
            <div style={{ ...styles.relImg, backgroundImage: img ? `url(${img})` : undefined, background: img ? undefined : '#DDE4F2' }} />
            <div style={styles.relTitle}>{ev.title}</div>
            {first?.start_date && <div style={styles.relDate}>{fmtChip(first.start_date)}</div>}
        </button>
    );
};

const spinKeyframes = `@keyframes spin { to { transform: rotate(360deg); } }`;

const styles: Record<string, React.CSSProperties> = {
    page: { minHeight: '100vh', background: SCREEN_BG, fontFamily: FONT_FAMILY, display: 'flex', justifyContent: 'center' },
    column: { width: '100%', maxWidth: 560, background: SCREEN_BG, position: 'relative', boxShadow: '0 0 40px rgba(27,37,89,0.06)' },
    center: { minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: SCREEN_BG },
    spinner: { width: 38, height: 38, border: `3px solid #DCE3F2`, borderTop: `3px solid ${ACCENT_SOFT}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

    hero: { position: 'relative', width: '100%', height: 360, background: '#000' },
    gallery: { display: 'flex', width: '100%', height: '100%', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none' },
    heroImg: { width: '100%', height: '100%', objectFit: 'cover', flex: '0 0 100%', scrollSnapAlign: 'start', cursor: 'pointer', display: 'block' },
    heroBar: { position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    circleBtn: { width: 40, height: 40, borderRadius: 20, border: 'none', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.18)' },
    counter: { position: 'absolute', bottom: 12, right: 14, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, padding: '4px 10px', borderRadius: 12 },

    body: { background: SCREEN_BG, borderTopLeftRadius: 22, borderTopRightRadius: 22, marginTop: -22, position: 'relative', padding: '22px 18px 0' },
    notice: { background: '#FDECEC', color: '#C0392B', borderRadius: 12, padding: '10px 14px', fontSize: 13.5, fontWeight: 600, marginBottom: 14 },
    poolBadge: { display: 'inline-flex', alignItems: 'center', minHeight: 26, padding: '0 9px', marginBottom: 8, borderRadius: 999, background: '#EAF1FF', color: ACCENT, fontSize: 12, fontWeight: 850 },
    title: { margin: '0 0 14px', fontSize: 24, fontWeight: 900, color: TEXT_DARK, lineHeight: 1.25 },
    poolSelector: { display: 'flex', gap: 10, overflowX: 'auto', padding: '2px 0 14px', marginBottom: 2, scrollbarWidth: 'none' },
    poolOption: { flex: '0 0 178px', minHeight: 92, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: 12, border: '1px solid #E4E7F1', borderRadius: 14, background: '#fff', color: TEXT_DARK, textAlign: 'left', cursor: 'pointer', boxShadow: '0 2px 10px rgba(27,37,89,0.04)' },
    poolOptionActive: { borderColor: ACCENT, boxShadow: '0 8px 18px rgba(78,111,214,0.14)' },
    poolOptionTitle: { width: '100%', color: TEXT_DARK, fontSize: 13.5, fontWeight: 850, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    poolOptionDate: { color: TEXT_MUTED, fontSize: 12, fontWeight: 700 },
    poolOptionPrice: { marginTop: 'auto', color: ACCENT, fontSize: 12.5, fontWeight: 850 },

    chipRow: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, scrollbarWidth: 'none' },
    chip: { flex: '0 0 auto', padding: '7px 14px', borderRadius: 20, border: '1px solid #D5DEEC', background: '#fff', color: TEXT_MUTED, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    chipActive: { background: ACCENT, borderColor: ACCENT, color: '#fff' },

    infoRow: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #ECEFF7' },
    infoStrong: { fontSize: 15, fontWeight: 700, color: TEXT_DARK },
    infoSub: { fontSize: 13.5, color: TEXT_MUTED, marginTop: 2 },
    mapPreviewCard: { position: 'relative', overflow: 'hidden', minHeight: 132, marginTop: 14, borderRadius: 16, background: '#EAF1FF', border: '1px solid #DCE6FA' },
    mapPreviewGrid: { position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', opacity: 0.64 },
    mapPreviewContent: { position: 'relative', zIndex: 1, padding: 16, maxWidth: 360 },
    mapPreviewTitle: { color: TEXT_DARK, fontSize: 16, fontWeight: 850 },
    mapPreviewAddress: { color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.45, marginTop: 4 },
    mapPreviewBtn: { marginTop: 12, minHeight: 36, padding: '0 13px', border: 'none', borderRadius: 999, background: ACCENT, color: '#fff', fontSize: 13, fontWeight: 850, cursor: 'pointer' },

    hostCard: { display: 'flex', gap: 12, alignItems: 'center', background: '#fff', borderRadius: 16, padding: 12, marginTop: 16, color: 'inherit', textDecoration: 'none' },
    hostAvatar: { width: 46, height: 46, borderRadius: 23, objectFit: 'cover', background: '#E7EEFC' },
    hostName: { fontSize: 15, fontWeight: 800, color: TEXT_DARK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    hostType: { fontSize: 12.5, color: TEXT_MUTED, marginTop: 1 },

    sectionLabel: { fontSize: 16, fontWeight: 800, color: TEXT_DARK, marginBottom: 10 },
    desc: { fontSize: 14.5, lineHeight: 1.6, color: '#3A4668', whiteSpace: 'pre-wrap', margin: 0 },
    descHtml: { whiteSpace: 'normal' },
    descClamp: { display: '-webkit-box', WebkitLineClamp: 20, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxHeight: '32em' },
    moreBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 40, marginTop: 12, border: 'none', borderRadius: 999, background: ACCENT, color: '#fff', fontWeight: 850, fontSize: 14, cursor: 'pointer', padding: '0 18px', boxShadow: '0 8px 18px rgba(78,111,214,0.22)' },

    infoCard: { background: '#fff', borderRadius: 16, padding: '4px 16px' },
    infoItem: { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '1px solid #ECEFF7' },
    infoItemLabel: { fontSize: 13.5, color: TEXT_MUTED, flex: '0 0 auto' },
    infoItemValue: { fontSize: 14, fontWeight: 600, color: TEXT_DARK, textAlign: 'right' },
    infoLink: { color: ACCENT, fontWeight: 800, textDecoration: 'none' },

    relRow: { display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' },
    relCard: { flex: '0 0 160px', width: 160, textAlign: 'left', border: 'none', background: '#fff', borderRadius: 14, padding: 0, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 2px 10px rgba(27,37,89,0.06)' },
    relImg: { width: '100%', height: 96, backgroundSize: 'cover', backgroundPosition: 'center' },
    relTitle: { fontSize: 13.5, fontWeight: 700, color: TEXT_DARK, padding: '8px 10px 2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    relDate: { fontSize: 12, color: TEXT_MUTED, padding: '0 10px 10px' },

    footer: { position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 900, padding: '12px 18px', background: 'rgba(246,247,251,0.96)', backdropFilter: 'blur(8px)', borderTop: '1px solid #ECEFF7' },
    cta: { width: '100%', padding: '15px', borderRadius: 14, border: 'none', background: ACCENT, color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer' },
    ctaDisabled: { background: '#B9C2DA', cursor: 'not-allowed' },

    lightbox: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
    lightboxClose: { position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: 20, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 18, cursor: 'pointer' },

    toast: { position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)', background: TEXT_DARK, color: '#fff', padding: '10px 18px', borderRadius: 20, fontSize: 13.5, fontFamily: FONT_FAMILY, zIndex: 3100 },
};

export default EventViewComponent;
